/**
 * Selection + bulk "Mark Delivered" state for the ASN list.
 *
 * Only ASNs in the `in_transit` status are selectable — those are the ones
 * the per-ASN mark-delivered endpoint accepts a delivered transition from in
 * the supplier-portal flow. The bulk action calls the existing per-ASN
 * `markASNDelivered` endpoint once per selected ASN (sequentially, so the
 * POChangeLog audit rows it writes stay ordered) and surfaces a single
 * summary toast plus a grid refresh.
 *
 * Extracted into its own hook so {@link ASNListTab} stays under the
 * 500-line file limit.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { purchasingAPI, AdvancedShippingNotice } from '../../../lib/api';

/** An ASN may be selected for the bulk action only in this status. */
export const BULK_DELIVERABLE_STATUS = 'in_transit';

interface UseASNBulkDeliveredArgs {
  /** Current page of ASNs (used to resolve "select all on page"). */
  asns: AdvancedShippingNotice[];
  /** Called after a bulk run completes so the grid can re-fetch. */
  onRefresh: () => void;
}

interface UseASNBulkDeliveredResult {
  /** Set of selected ASN ids. */
  selectedIds: Set<number>;
  /** True while a bulk mark-delivered run is in flight. */
  marking: boolean;
  /** Whether a given ASN row may be selected. */
  isSelectable: (asn: AdvancedShippingNotice) => boolean;
  /** Toggle a single ASN's selection (no-op if not selectable). */
  toggleSelect: (asn: AdvancedShippingNotice) => void;
  /** Toggle all selectable ASNs on the current page. */
  toggleSelectAll: () => void;
  /** Clear the entire selection. */
  clearSelection: () => void;
  /** Run mark-delivered for every selected ASN, then refresh. */
  markSelectedDelivered: () => Promise<void>;
}

/**
 * Manage row selection and the bulk mark-delivered action for the ASN list.
 *
 * @param args.asns - the current page of ASNs
 * @param args.onRefresh - callback to re-fetch the grid after a bulk run
 * @returns selection state and handlers for the table + action bar
 */
export function useASNBulkDelivered({
  asns,
  onRefresh,
}: UseASNBulkDeliveredArgs): UseASNBulkDeliveredResult {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [marking, setMarking] = useState(false);

  const isSelectable = useCallback(
    (asn: AdvancedShippingNotice) => asn.status === BULK_DELIVERABLE_STATUS,
    [],
  );

  const toggleSelect = useCallback(
    (asn: AdvancedShippingNotice) => {
      if (asn.status !== BULK_DELIVERABLE_STATUS) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(asn.id)) next.delete(asn.id);
        else next.add(asn.id);
        return next;
      });
    },
    [],
  );

  const toggleSelectAll = useCallback(() => {
    const selectable = asns.filter((a) => a.status === BULK_DELIVERABLE_STATUS);
    setSelectedIds((prev) => {
      const allSelected =
        selectable.length > 0 && selectable.every((a) => prev.has(a.id));
      if (allSelected) {
        const next = new Set(prev);
        selectable.forEach((a) => next.delete(a.id));
        return next;
      }
      const next = new Set(prev);
      selectable.forEach((a) => next.add(a.id));
      return next;
    });
  }, [asns]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const markSelectedDelivered = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setMarking(true);
    let succeeded = 0;
    let failed = 0;
    // Sequential loop (not Promise.all) so the per-ASN POChangeLog audit rows
    // are written in a deterministic order and we don't hammer the API.
    for (const id of ids) {
      try {
        await purchasingAPI.markASNDelivered(id, {});
        succeeded += 1;
      } catch (err: unknown) {
        failed += 1;
        console.error(`Failed to mark ASN ${id} delivered:`, err);
      }
    }
    setMarking(false);
    setSelectedIds(new Set());

    if (failed === 0) {
      toast.success(
        `Marked ${succeeded} ASN${succeeded === 1 ? '' : 's'} as delivered`,
      );
    } else if (succeeded === 0) {
      toast.error(
        `Failed to mark ${failed} ASN${failed === 1 ? '' : 's'} as delivered`,
      );
    } else {
      toast.warning(
        `Marked ${succeeded} delivered; ${failed} failed`,
      );
    }
    onRefresh();
  }, [selectedIds, onRefresh]);

  return {
    selectedIds,
    marking,
    isSelectable,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    markSelectedDelivered,
  };
}
